/**
 * NewFileDialog
 *
 * A dialog that lets the user pick a file type and enter a filename to create
 * a new blank (template-seeded) project file.
 *
 * Supported creatable types:
 *   scad   — OpenSCAD module stub
 *   svg    — Minimal SVG skeleton
 *   js     — Shader script (vertexShader + fragmentShader exports)
 *   js     — Texture script (Canvas 2D sendFrame loop)
 *   json   — Empty JSON object
 *   txt    — Empty text file
 *   dat    — Height-map data file with comment header
 *   dxf    — Minimal DXF skeleton
 */

import { useState, useEffect, useCallback } from 'react';
import {
  FileCode,
  Image,
  Cpu,
  Wand2,
  Braces,
  FileText,
  BarChart2,
  Layers,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { ModelFileType } from '@/lib/supabase/types';
import {
  SCAD_MODULE_TEMPLATE,
  SVG_TEMPLATE,
  JS_SHADER_TEMPLATE,
  JS_TEXTURE_TEMPLATE,
  JSON_TEMPLATE,
  TXT_TEMPLATE,
  DAT_TEMPLATE,
  DXF_TEMPLATE,
} from '@/lib/file-templates';

// ── Type definitions ──────────────────────────────────────────────────────────

/** All creatable file kinds.  "js-shader" and "js-texture" both map to the
 *  "js" ModelFileType but receive different template content. */
export type CreatableKind =
  | 'scad'
  | 'svg'
  | 'js-shader'
  | 'js-texture'
  | 'json'
  | 'txt'
  | 'dat'
  | 'dxf';

interface FileKindMeta {
  kind: CreatableKind;
  label: string;
  description: string;
  extension: string;
  fileType: ModelFileType;
  defaultName: string;
  icon: React.ReactNode;
  template: string;
}

const FILE_KINDS: FileKindMeta[] = [
  {
    kind: 'scad',
    label: 'OpenSCAD module',
    description: 'Importable .scad module file',
    extension: 'scad',
    fileType: 'scad',
    defaultName: 'my_module.scad',
    icon: <FileCode className="h-5 w-5 text-blue-500" />,
    template: SCAD_MODULE_TEMPLATE,
  },
  {
    kind: 'svg',
    label: 'SVG',
    description: 'Vector graphic for import or texture',
    extension: 'svg',
    fileType: 'svg',
    defaultName: 'shape.svg',
    icon: <Image className="h-5 w-5 text-orange-500" />,
    template: SVG_TEMPLATE,
  },
  {
    kind: 'js-shader',
    label: 'Shader script',
    description: 'GLSL vertex + fragment shader via shader_material()',
    extension: 'js',
    fileType: 'js',
    defaultName: 'my_shader.js',
    icon: <Cpu className="h-5 w-5 text-violet-500" />,
    template: JS_SHADER_TEMPLATE,
  },
  {
    kind: 'js-texture',
    label: 'Texture script',
    description: 'Canvas 2D texture via texture_script()',
    extension: 'js',
    fileType: 'js',
    defaultName: 'my_texture.js',
    icon: <Wand2 className="h-5 w-5 text-amber-400" />,
    template: JS_TEXTURE_TEMPLATE,
  },
  {
    kind: 'json',
    label: 'JSON',
    description: 'Structured data for use in SCAD scripts',
    extension: 'json',
    fileType: 'json',
    defaultName: 'data.json',
    icon: <Braces className="h-5 w-5 text-emerald-500" />,
    template: JSON_TEMPLATE,
  },
  {
    kind: 'txt',
    label: 'Text file',
    description: 'Plain text or notes',
    extension: 'txt',
    fileType: 'txt',
    defaultName: 'notes.txt',
    icon: <FileText className="h-5 w-5 text-muted-foreground" />,
    template: TXT_TEMPLATE,
  },
  {
    kind: 'dat',
    label: 'DAT height map',
    description: 'Grid of floats for surface() height maps',
    extension: 'dat',
    fileType: 'dat',
    defaultName: 'heightmap.dat',
    icon: <BarChart2 className="h-5 w-5 text-cyan-500" />,
    template: DAT_TEMPLATE,
  },
  {
    kind: 'dxf',
    label: 'DXF drawing',
    description: '2D vector drawing for import()',
    extension: 'dxf',
    fileType: 'dxf',
    defaultName: 'drawing.dxf',
    icon: <Layers className="h-5 w-5 text-green-500" />,
    template: DXF_TEMPLATE,
  },
];

// ── Helper ────────────────────────────────────────────────────────────────────

/** Replace the extension of a filename with `newExt`. Preserves the base name. */
function swapExtension(filename: string, newExt: string): string {
  const dotIdx = filename.lastIndexOf('.');
  const base = dotIdx > 0 ? filename.slice(0, dotIdx) : filename;
  return `${base}.${newExt}`;
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface NewFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms creation.  The caller is responsible for
   *  deduplication and calling onFileAdd. */
  onCreateFile: (filename: string, content: string, fileType: ModelFileType) => void;
  /** Existing filenames (used for duplicate-name validation). */
  existingFilenames?: string[];
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NewFileDialog({
  open,
  onOpenChange,
  onCreateFile,
  existingFilenames = [],
}: NewFileDialogProps) {
  const [selectedKind, setSelectedKind] = useState<CreatableKind>('scad');
  const [filename, setFilename] = useState('my_module.scad');
  const [filenameError, setFilenameError] = useState<string | null>(null);

  const meta = FILE_KINDS.find((k) => k.kind === selectedKind)!;

  // When the dialog opens, reset to default selection
  useEffect(() => {
    if (open) {
      setSelectedKind('scad');
      setFilename('my_module.scad');
      setFilenameError(null);
    }
  }, [open]);

  // When the kind changes, update the extension in the current filename
  const handleKindChange = useCallback(
    (kind: CreatableKind) => {
      setSelectedKind(kind);
      const newMeta = FILE_KINDS.find((k) => k.kind === kind)!;
      setFilename((prev) => swapExtension(prev || newMeta.defaultName, newMeta.extension));
      setFilenameError(null);
    },
    [],
  );

  const validateFilename = useCallback(
    (name: string): string | null => {
      const trimmed = name.trim();
      if (!trimmed) return 'Filename cannot be empty.';
      if (!/^[a-zA-Z0-9_\-. ]+$/.test(trimmed))
        return 'Only letters, numbers, spaces, _ - . are allowed.';
      if (existingFilenames.includes(trimmed))
        return `A file named "${trimmed}" already exists.`;
      return null;
    },
    [existingFilenames],
  );

  const handleFilenameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setFilename(val);
      setFilenameError(validateFilename(val));
    },
    [validateFilename],
  );

  const handleCreate = useCallback(() => {
    const trimmed = filename.trim();
    const error = validateFilename(trimmed);
    if (error) {
      setFilenameError(error);
      return;
    }
    onCreateFile(trimmed, meta.template, meta.fileType);
    onOpenChange(false);
  }, [filename, meta, validateFilename, onCreateFile, onOpenChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleCreate();
    },
    [handleCreate],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New file</DialogTitle>
          <DialogDescription>
            Choose a file type and enter a filename. The file will be
            pre-filled with a minimal starter template.
          </DialogDescription>
        </DialogHeader>

        {/* File type grid */}
        <div className="grid grid-cols-2 gap-2 py-1">
          {FILE_KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              onClick={() => handleKindChange(k.kind)}
              className={cn(
                'flex items-start gap-3 rounded-md border p-3 text-left transition-colors hover:bg-accent/50',
                selectedKind === k.kind
                  ? 'border-primary bg-primary/10'
                  : 'border-border',
              )}
            >
              <span className="mt-0.5 shrink-0">{k.icon}</span>
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-tight">
                  {k.label}
                </span>
                <span className="block text-xs text-muted-foreground leading-snug mt-0.5">
                  {k.description}
                </span>
              </span>
            </button>
          ))}
        </div>

        {/* Filename input */}
        <div className="flex flex-col gap-1.5 pt-1">
          <Label htmlFor="new-file-name">Filename</Label>
          <Input
            id="new-file-name"
            value={filename}
            onChange={handleFilenameChange}
            onKeyDown={handleKeyDown}
            placeholder={meta.defaultName}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className={cn(filenameError && 'border-destructive')}
          />
          {filenameError && (
            <p className="text-xs text-destructive">{filenameError}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!!filenameError}>
            Create file
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
